import { supabase } from '@tickitacka/shared';
import { authService } from '@/features/auth/services/authService';
import { ReportReceiver } from '../types/models';
import {
    ReportReceiverWithUser,
    PostgresError,
    ReportReceiverResult,
} from '../types/reportTypes';

/**
 * 보고서 수신자 관리 서비스
 */
export const reportReceiverService = {
    /**
     * 보고서에서 수신자를 제거합니다
     * @param receiverId 수신자 ID
     */
    async removeReportReceiver(receiverId: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('report_receiver')
                .delete()
                .eq('id', receiverId);

            if (error) throw error;
        } catch (error) {
            console.error('보고서 수신자 제거 실패:', error);
            throw error;
        }
    },

    /**
     * 기존 수신자의 읽음 상태를 초기화합니다
     * @param reportId 보고서 ID
     * @param userId 사용자 ID
     * @returns 업데이트된 수신자 정보
     */
    async resetExistingReportReceiver(
        reportId: string,
        userId: string
    ): Promise<ReportReceiver> {
        try {
            // 해당 수신자 레코드 조회
            const { data: receivers, error: fetchError } = await supabase
                .from('report_receiver')
                .select('id')
                .eq('report_id', reportId)
                .eq('user_id', userId)
                .eq('enable', true)
                .single();

            if (fetchError) throw fetchError;
            if (!receivers) throw new Error('수신자 정보를 찾을 수 없습니다.');

            // 읽음 상태 초기화
            const { data: updatedReceiver, error: updateError } = await supabase
                .from('report_receiver')
                .update({
                    is_read: false,
                    update_date: new Date().toISOString(),
                })
                .eq('id', receivers.id)
                .select()
                .single();

            if (updateError) throw updateError;

            return updatedReceiver;
        } catch (error) {
            console.error('수신자 읽음 상태 초기화 중 오류:', error);
            throw error;
        }
    },

    /**
     * 보고서를 읽음으로 표시합니다
     * @param receiverId 수신자 ID
     * @returns 업데이트된 수신자 정보
     */
    async markReportAsRead(receiverId: string): Promise<ReportReceiver> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('report_receiver')
                .update({
                    is_read: true,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', receiverId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('보고서 읽음 표시 실패:', error);
            throw error;
        }
    },

    /**
     * 보고서의 수신자 목록을 조회합니다
     * @param reportId 보고서 ID
     * @returns 수신자 목록
     */
    async getReportReceivers(
        reportId: string
    ): Promise<ReportReceiverWithUser[]> {
        try {
            const { data, error } = await supabase
                .from('report_receiver')
                .select(
                    `
                    *,
                    user:user_id (
                        id, name, profile_image_url
                    )
                    `
                )
                .eq('report_id', reportId)
                .eq('enable', true);

            if (error) throw error;
            return (data || []) as unknown as ReportReceiverWithUser[];
        } catch (error) {
            console.error('보고서 수신자 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 보고서에 여러 수신자를 일괄 추가합니다
     * @param reportId 보고서 ID
     * @param userIds 사용자 ID 목록
     * @param isPrimary 주 수신자 여부
     * @returns 새로 추가된 수신자와 업데이트된 수신자 목록
     */
    async addReportReceivers(
        reportId: string,
        userIds: string[],
        isPrimary = false
    ): Promise<ReportReceiverResult> {
        try {
            const now = new Date().toISOString();
            const currentUser = await authService.getCurrentUser();
            if (!currentUser)
                throw new Error('사용자가 로그인되어 있지 않습니다.');

            // 1. 이미 존재하는 수신자 확인
            const { data: existingReceivers } = await supabase
                .from('report_receiver')
                .select('user_id')
                .eq('report_id', reportId)
                .in('user_id', userIds)
                .eq('enable', true);

            const existingUserIds = new Set(
                (existingReceivers || []).map(r => r.user_id)
            );

            // 2. 새로운 수신자만 추가
            const newUserIds = userIds.filter(id => !existingUserIds.has(id));
            let newReceivers: string[] = [];

            if (newUserIds.length > 0) {
                const receiversData = newUserIds.map(userId => ({
                    report_id: reportId,
                    user_id: userId,
                    is_primary: isPrimary,
                    is_read: false,
                    enable: true,
                    register_date: now,
                    register_id: currentUser.id,
                    update_date: now,
                }));

                const { error: insertError } = await supabase
                    .from('report_receiver')
                    .insert(receiversData);

                if (insertError) throw insertError;
                newReceivers = newUserIds;
            }

            // 3. 기존 수신자 읽음 상태 초기화
            const existingUserIdsArray = Array.from(existingUserIds);
            let updatedReceivers: string[] = [];

            if (existingUserIdsArray.length > 0) {
                const { error: updateError } = await supabase
                    .from('report_receiver')
                    .update({
                        is_read: false,
                        update_date: now,
                        update_id: currentUser.id,
                    })
                    .eq('report_id', reportId)
                    .in('user_id', existingUserIdsArray);

                if (updateError) throw updateError;
                updatedReceivers = existingUserIdsArray;
            }

            return {
                newReceivers,
                updatedReceivers,
                report_id: reportId,
            };
        } catch (error) {
            console.error('보고서 수신자 추가 실패:', error);
            throw error;
        }
    },

    /**
     * 보고서 수정 시 모든 수신자의 읽음 상태를 초기화합니다
     * @param reportId 보고서 ID
     */
    async resetReportReadStatus(reportId: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('report_receiver')
                .update({
                    is_read: false,
                    update_date: new Date().toISOString(),
                })
                .eq('report_id', reportId)
                .eq('enable', true);

            if (error) {
                console.error('수신자 읽음 상태 초기화 중 오류 발생:', error);
                throw error;
            }

            console.log(
                `보고서 ID ${reportId}의 모든 수신자 읽음 상태가 초기화되었습니다.`
            );
        } catch (error) {
            console.error('수신자 읽음 상태 초기화 실패:', error);
            throw error;
        }
    },

    /**
     * PostgreSQL 고유 제약 위반 오류인지 확인합니다
     * @param error 오류 객체
     * @returns 고유 제약 위반 오류 여부
     */
    isUniqueViolation(error: unknown): error is PostgresError {
        return (
            typeof error === 'object' &&
            error !== null &&
            'code' in error &&
            (error as PostgresError).code === '23505' &&
            'message' in error &&
            typeof (error as PostgresError).message === 'string' &&
            (error as PostgresError).message.includes('report_receiver_unique')
        );
    },
};
